import { useCallback } from 'react';
import { documentTypesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { DocumentType } from '../types';

export function useDocumentTypes(
  options?: ResourceQueryOptions
): ResourceState<DocumentType> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      documentTypesService.getAll(opts) as unknown as Promise<{ data: DocumentType[]; meta?: any }>,
    []
  );
  return useResourceCollection<DocumentType>(fetchFn, options);
}

export function useDocumentType(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<DocumentType> {
  const resourceId = id;
  const fetchFn = useCallback(
    (docTypeId: string | number, opts?: ResourceQueryOptions) =>
      documentTypesService.getById(docTypeId, opts) as unknown as Promise<DocumentType>,
    []
  );
  return useResourceById<DocumentType>(fetchFn, resourceId, options);
}

export function useCreateDocumentType(
  onSuccess?: (data: DocumentType) => void
): MutationState<DocumentType> {
  const createFn = useCallback(
    (data: any) => documentTypesService.create(data) as unknown as Promise<DocumentType>,
    []
  );
  return useCreateResource<DocumentType>(createFn, onSuccess);
}

export function useUpdateDocumentType(
  onSuccess?: (data: DocumentType) => void
): MutationState<DocumentType> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      return documentTypesService.update(id, data) as unknown as Promise<DocumentType>;
    },
    []
  );
  return useUpdateResource<DocumentType>(updateFn, onSuccess);
}

/**
 * Delete document type using documentId
 * @param data - Full document type object containing documentId
 */
export function useDeleteDocumentType(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return documentTypesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

